import { useRef, useEffect } from 'react';
import type { Dispatch, SetStateAction, RefObject } from 'react';
import type ReactPlayer from 'react-player/youtube';

import { useGetAiPlayerItemQuery, useRunRecognizeVideoMutation } from '@shared/api';
import type { Locale } from '@shared/libs';
import { Loader } from '@shared/ui';

import type { TVideoItem } from '../../model';
import styles from '../ai-player.module.css';
import { Empty } from './empty';
import { Player } from './player';

type TProps = TVideoItem & {
  locale: Locale;
  mp3: string;
  setIsPlay: Dispatch<SetStateAction<boolean>>;
  isPlay: boolean;
  startMs: number | null;
  onChange: (val: number) => void;
  idNote: number;
  isVisible: boolean;
  refPlayer: RefObject<ReactPlayer>;
};

export const Active = ({
  locale,
  id,
  isPlay,
  setIsPlay,
  mp3,
  startMs,
  onChange,
  idNote,
  isVisible,
  refPlayer,
}: TProps) => {
  const firstVisible = useRef(false);
  const { data, isError, isFetching } = useGetAiPlayerItemQuery({ id, idNote });
  const mutation = useRunRecognizeVideoMutation();

  const status = data?.status;

  useEffect(() => {
    if (isVisible) {
      if (!firstVisible.current && status === 'empty') {
        if (process.env.NEXT_PUBLIC_VISIBLE_AUTO_RECOGNIZE === 'true') {
          mutation.mutate({ id });
        }
      }
      firstVisible.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, status, id]);

  useEffect(() => {
    firstVisible.current = false;
  }, [idNote]);

  if (isFetching && status !== 'recognize') {
    return (
      <div className={styles.loaderWrap}>
        <Loader />
      </div>
    );
  }

  if (status === 'error' || isError) {
    // return <Empty locale={locale} id={id} error />;
    return <Empty locale={locale} id={id} error />;
  }

  if (status === 'empty') {
    return <Empty locale={locale} id={id} />;
  }

  if (status === 'recognize') {
    let completed = data?.data?.done_percent;
    //       typeof data?.data?.estimated_time === 'number' && completedFull
    //           // eslint-disable-next-line no-unsafe-optional-chaining
    //         ? 100 - (100 * data?.data?.estimated_time) / completedFull
    //         : undefined;
    // done_percent;
    if (completed) {
      if (completed < 80) {
        completed = 10 + completed;
      }
      if (completed > 80) {
        completed = 80;
      }

      completed = Math.round(completed);
    }

    return <Empty locale={locale} id={id} recognize completed={completed || 10} />;
  }

  if (status === 'waiting') {
    return <Empty locale={locale} id={id} waiting />;
  }

  if (!data?.success || !data?.data || !status || !data?.data?.chords) {
    return null;
  }

  return (
    <Player
      locale={locale}
      chords={data.data.chords}
      mp3={mp3}
      isPlay={isPlay}
      setIsPlay={setIsPlay}
      startMs={startMs}
      onChange={onChange}
      refPlayer={refPlayer}
    />
  );
};
